import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'gatsby';
import styled from 'styled-components';
import breakpoint from 'styled-components-breakpoint';

import PlayStoreBadge from '../assets/images/badge-google-play.png';
import AppStoreBadge from '../assets/images/badge-app-store.png';
import { getLanguageCode, useLocalizedSiteBottom } from '../utils';

import Block from './Block';
import Grid from './Grid';
import Cell from './Cell';
import H5 from './H5';
import P from './P';
import Image from './Image';
import Logo from './Logo';

const StoreLink = styled.a`
  &:nth-of-type(1) {
    margin-bottom: 16px;
  }

  ${breakpoint('tablet')`
    &:nth-of-type(1) {
      margin-bottom: 0;
      margin-right: 16px
    }
  `}
`;
const StoreBadge = styled.img`
  height: 48px;
  width: auto;

  ${breakpoint('tablet')`
    height: 40px;
  `}
`;

const SocialLinkAnchor = styled.a`
  width: 25px;
`;

const SiteBottom = ({ selectedLocale }) => {
  const [siteBottom] = useLocalizedSiteBottom(selectedLocale);
  const content = siteBottom.node.data;

  return (
    <Block
      as="footer"
      backgroundColor="grayLight"
      py={{ mobile: 'baseUp4', tablet: 'baseUp8' }}
    >
      <Grid>
        <Cell size={{ mobile: 1, tablet: 1 / 3 }}>
          <Block
            display="flex"
            justifyContent={{ mobile: 'center', tablet: 'inherit' }}
            px="baseUnit"
            py={{ mobile: 'baseUnit', tablet: '0' }}
          >
            <Link
              to={
                selectedLocale === 'en-us'
                  ? '/'
                  : `/${getLanguageCode(selectedLocale)}`
              }
            >
              <Logo
                height={{ mobile: '55px', tablet: '80px' }}
                alt="QuizRR home"
                color="orange"
              />
            </Link>
          </Block>
          <Block
            display="flex"
            flexDirection={{ mobile: 'column', tablet: 'row' }}
            justifyContent={{ mobile: 'center', tablet: 'flex-start' }}
            alignItems="center"
            px="baseUnit"
            py={{ mobile: 'baseDown2', tablet: 'baseUp2' }}
          >
            <StoreLink
              href="https://play.google.com/store/apps/details?id=com.quizrr.app.mobile"
              target="_blank"
            >
              <StoreBadge src={PlayStoreBadge} />
            </StoreLink>
            <StoreLink
              href="https://apps.apple.com/us/app/quizrr/id1557597408"
              target="_blank"
            >
              <StoreBadge src={AppStoreBadge} />
            </StoreLink>
          </Block>
        </Cell>

        <Cell size={{ mobile: 1, tablet: 1 / 12 }} />

        <Cell size={{ mobile: 1, tablet: 1 / 4 }}>
          <Block p="baseUnit">
            <H5
              color="grayDarker"
              textAlign={{ mobile: 'center', tablet: 'left' }}
              mb={{ mobile: '0', tablet: 'baseDown2' }}
            >
              {content.sitemap_title}
            </H5>

            {content.sitemap &&
              content.sitemap.length &&
              content.sitemap.map(({ title, link }) => {
                const localePath =
                  selectedLocale === 'en-us'
                    ? ''
                    : `/${getLanguageCode(selectedLocale)}`;
                const toPath = `${localePath}${
                  link.url === '/index' ? '/' : link.url
                }`;

                return (
                  <P
                    key={`sitemap-${link.url}`}
                    mb="baseDown3"
                    textAlign={{ mobile: 'center', tablet: 'left' }}
                    isBold
                  >
                    {link.link_type === 'Web' ? (
                      <a href={link.url} target={link.target}>
                        {title}
                      </a>
                    ) : (
                      <Link to={toPath}>{title}</Link>
                    )}
                  </P>
                );
              })}
          </Block>
        </Cell>

        <Cell size={{ mobile: 1, tablet: 1 / 4 }}>
          <Block p="baseUnit">
            <H5
              color="grayDarker"
              textAlign={{ mobile: 'center', tablet: 'left' }}
              mb={{ mobile: '0', tablet: 'baseDown2' }}
            >
              {content.contacts_title}
            </H5>
            <Grid>
              <Cell size={{ mobile: 1, tablet: 5 / 6 }}>
                <Block mb={{ mobile: 'baseUnit', tablet: '0' }}>
                  {(content.contacts &&
                    content.contacts.length &&
                    content.contacts.map(({ title, link }, i) => (
                      <P
                        key={`contact-${link.url}-${i}`}
                        mb="baseDown3"
                        textAlign={{ mobile: 'center', tablet: 'left' }}
                        isBold
                      >
                        <a href={link.url}>{title}</a>
                      </P>
                    ))) ||
                    null}
                </Block>
              </Cell>
              <Cell size={{ mobile: 1, tablet: 1 / 6 }}>
                {(content.social_links &&
                  content.social_links.length &&
                  content.social_links.map((socialLink, i) => (
                    <Block
                      key={`social-link-${socialLink.href.url}-${i}`}
                      mb={
                        i < content.social_links.length - 1 ? 'baseUnit' : '0'
                      }
                      pt={{ mobile: i === 0 ? 'baseUnit' : '0', tablet: '0' }}
                      display="flex"
                      justifyContent="center"
                    >
                      <SocialLinkAnchor href={socialLink.href.url}>
                        <Image content={socialLink.icon} />
                      </SocialLinkAnchor>
                    </Block>
                  ))) ||
                  null}
              </Cell>
            </Grid>
          </Block>
        </Cell>
      </Grid>

      <Grid>
        <Cell size={1}>
          <P
            color="grayDarker"
            mb="0"
            px="baseUnit"
            fontSize="baseDown1"
            textAlign={{ mobile: 'center', tablet: 'left' }}
          >
            &copy; Quizrr 2022
          </P>
        </Cell>
      </Grid>

      {/* Google Play and the Google Play logo are trademarks of Google LLC. */}
      {/* App Store and the Apple logo are trademarks of Apple Inc. */}
    </Block>
  );
};

SiteBottom.propTypes = {
  selectedLocale: PropTypes.string.isRequired,
};

export default SiteBottom;
